import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { COLLECTIONS, LANDING_DOCS, type AssetDoc, type LandingContent } from '@brands/shared';
import { useAuth } from '../auth/AuthContext';
import { firebase } from '../lib/firebase';
import type { Asset, Loadable } from './campaign';

/**
 * Contenido de la landing: la versión publicada (cliente) o el borrador guardado (vista previa
 * del admin; las reglas solo dejan leer el borrador a administradores).
 */
export function useLandingContent(
  campaignId: string | undefined,
  which: keyof typeof LANDING_DOCS,
): Loadable<LandingContent> {
  const [state, setState] = useState<Loadable<LandingContent>>({ status: 'loading' });
  useEffect(() => {
    if (!campaignId) return;
    return onSnapshot(
      doc(
        firebase().db,
        COLLECTIONS.campaigns,
        campaignId,
        COLLECTIONS.landing,
        LANDING_DOCS[which],
      ),
      (snap) => {
        const content = snap.get('content') as LandingContent | undefined;
        setState(content ? { status: 'ready', data: content } : { status: 'missing' });
      },
      () => setState({ status: 'missing' }),
    );
  }, [campaignId, which]);
  return state;
}

export const usePublishedLanding = (campaignId: string | undefined) =>
  useLandingContent(campaignId, 'published');

/** Entregables visibles (la consulta filtra contexto, como exigen las reglas). */
export function useDeliverables(campaignId: string | undefined): Loadable<Map<string, Asset>> {
  const [state, setState] = useState<Loadable<Map<string, Asset>>>({ status: 'loading' });
  useEffect(() => {
    if (!campaignId) return;
    return onSnapshot(
      query(
        collection(firebase().db, COLLECTIONS.campaigns, campaignId, COLLECTIONS.assets),
        where('isContextDoc', '==', false),
      ),
      (snap) => {
        const map = new Map<string, Asset>();
        for (const d of snap.docs) {
          const a = { ...(d.data() as AssetDoc), id: d.id };
          if (!a.removedAt && a.copyStatus === 'copied') map.set(a.id, a);
        }
        setState({ status: 'ready', data: map });
      },
      () => setState({ status: 'error', message: 'No pudimos cargar los archivos.' }),
    );
  }, [campaignId]);
  return state;
}

/** Visita anterior por campaña, leída una sola vez por sesión de la página. */
const previousVisits = new Map<string, number | null>();

/**
 * Lee la visita anterior del cliente y registra la actual. Devuelve la fecha de la visita
 * anterior (null en la primera visita) para marcar lo nuevo desde entonces.
 */
export function useLastVisit(campaignId: string | undefined): number | null | undefined {
  const { session } = useAuth();
  const [fetched, setFetched] = useState<{ key: string; value: number | null } | null>(null);
  const cacheKey = session && campaignId ? `${session.user.uid}|${campaignId}` : null;
  // Los admins no son miembros: no registran visitas ni ven marcas de "nuevo".
  const isAdmin = Boolean(session?.isStaff);

  useEffect(() => {
    if (!campaignId || !session || !cacheKey || isAdmin || previousVisits.has(cacheKey)) return;
    const ref = doc(
      firebase().db,
      COLLECTIONS.campaigns,
      campaignId,
      COLLECTIONS.members,
      session.user.uid,
    );
    let cancelled = false;
    void (async () => {
      let value: number | null = null;
      try {
        const snap = await getDoc(ref);
        const last = snap.get('lastVisitAt') as Timestamp | undefined;
        value = last ? last.toMillis() : null;
        // Se guarda antes de escribir la visita actual: si el efecto se repite (StrictMode o
        // volver a la página), sigue comparando contra la visita anterior real.
        if (!previousVisits.has(cacheKey)) previousVisits.set(cacheKey, value);
        await setDoc(ref, { email: session.email, lastVisitAt: serverTimestamp() });
      } catch {
        if (!previousVisits.has(cacheKey)) previousVisits.set(cacheKey, null);
      }
      if (!cancelled) setFetched({ key: cacheKey, value: previousVisits.get(cacheKey) ?? value });
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId, session, cacheKey, isAdmin]);

  if (!cacheKey) return undefined;
  if (isAdmin) return null;
  if (previousVisits.has(cacheKey)) return previousVisits.get(cacheKey) ?? null;
  return fetched?.key === cacheKey ? fetched.value : undefined;
}

export const isNewSince = (asset: Asset, since: number | null | undefined): boolean => {
  if (!since) return false;
  const updated = asset.contentUpdatedAt as Timestamp | null | undefined;
  return Boolean(updated && typeof updated.toMillis === 'function' && updated.toMillis() > since);
};
